import { useParams, Link } from "react-router-dom";
import { useTour } from "../lib/useTours";
import { MapPin, Clock, Users, Star, Check, X, Mountain, ArrowLeft, Compass, Info, Route, Home, Bus, Calendar } from "lucide-react";
import BookingForm from "../components/BookingForm";
import WishlistButton from "../components/WishlistButton";
import ShareWhatsAppButton from "../components/ShareWhatsAppButton";

/**
 * Sub-section: cinematic tour hero.
 */
function TourHero({ tour }) {
  return (
    <section className="relative h-[70vh] overflow-hidden">
      <img src={tour.image} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D3A]/90 via-[#1F4D3A]/30 to-transparent" />
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 h-full flex flex-col justify-end pb-14">
        <Link to="/tours" className="inline-flex items-center gap-2 text-[#E5DDCB] hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to journeys
        </Link>
        <span className="inline-block bg-[#C87A50] text-white text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full mb-4 self-start">{tour.tag}</span>
        <h1 className="font-serif text-4xl md:text-6xl font-bold text-white leading-tight max-w-3xl">{tour.title}</h1>
        <div className="flex flex-wrap gap-6 mt-5 text-white/90 text-sm">
          <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {tour.location}</span>
          <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {tour.duration}</span>
          <span className="flex items-center gap-2"><Users className="w-4 h-4" /> {tour.groupSize} pax</span>
          <span className="flex items-center gap-2"><Mountain className="w-4 h-4" /> {tour.difficulty}</span>
          <span className="flex items-center gap-2"><Star className="w-4 h-4 fill-[#C9A961] text-[#C9A961]" /> {tour.rating} ({tour.reviews} reviews)</span>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <WishlistButton tourId={tour.id} variant="inline" />
          <ShareWhatsAppButton
            title={tour.title}
            subtitle={`${tour.duration} · ₹${tour.price.toLocaleString("en-IN")} per person`}
            path={`/tours/${tour.id}`}
            variant="inline"
            testId="tour-detail-share-btn"
          />
        </div>
      </div>
    </section>
  );
}

/**
 * Sub-section: trip details panel (Starting Point, Stay, Transport, Duration, Route).
 */
function TripInfoPanel({ tripInfo, duration }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5DDCB] p-5 sm:p-7">
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <Info className="w-5 h-5 text-[#C87A50]" />
        <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1F4D3A]">Trip details</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        {[
          { Icon: Compass, label: 'Starting point', value: tripInfo.startingPoint },
          { Icon: Home, label: 'Stay', value: tripInfo.stay },
          { Icon: Bus, label: 'Transport', value: tripInfo.transport },
          { Icon: Calendar, label: 'Duration', value: duration }
        ].map(({ Icon, label, value }) => (
          <div key={label} className="flex items-start gap-3">
            <Icon className="w-4 h-4 mt-1 text-[#6B8E5A] flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B8E5A] font-semibold mb-0.5">{label}</div>
              <div className="text-[#4A3728]">{value}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#E5DDCB] flex items-start gap-3">
        <Route className="w-4 h-4 mt-1 text-[#C87A50] flex-shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6B8E5A] font-semibold mb-0.5">Route</div>
          <div className="text-sm text-[#4A3728] leading-relaxed">{tripInfo.route}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sub-section: experience slot cards (used when a tour packs multiple experiences).
 */
function ExperienceSlots({ experiences }) {
  return (
    <div>
      <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-5 sm:mb-6">Two adventures, one weekend</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {experiences.map((exp, idx) => (
          <div key={exp.title} className="bg-white rounded-2xl overflow-hidden border border-[#E5DDCB] card-lift">
            <div className="relative aspect-[4/3] img-zoom">
              <img src={exp.image} alt={exp.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D3A]/85 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-[#C87A50] text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full">
                Day {idx + 1} · {exp.day}
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961] mb-1 font-semibold">Experience {idx + 1}</div>
                <div className="font-serif text-2xl font-bold leading-tight">{exp.title}</div>
                <div className="text-xs text-[#E5DDCB] mt-0.5">{exp.subtitle}</div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#4A3728] leading-relaxed mb-4">{exp.summary}</p>
              <div className="flex flex-wrap gap-1.5">
                {exp.features.map((f) => (
                  <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-[#F5F0E8] text-[#4A3728] border border-[#E5DDCB]">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Sub-section: day-by-day itinerary timeline.
 */
function ItineraryTimeline({ itinerary }) {
  return (
    <div>
      <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-5 sm:mb-6">Day by day</h2>
      <div className="space-y-4">
        {itinerary.map((d) => (
          <div key={d.day} className="flex gap-4 sm:gap-5">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1F4D3A] text-white flex items-center justify-center font-serif font-bold flex-shrink-0 text-sm sm:text-base">
                {d.day}
              </div>
              {d.day !== itinerary.length && <div className="w-0.5 flex-1 bg-[#E5DDCB] mt-2" />}
            </div>
            <div className="pb-6 flex-1">
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#1F4D3A] mb-1">{d.title}</h3>
              <p className="text-sm text-[#5A4A38] leading-relaxed">{d.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Sub-section: includes / excludes side-by-side cards.
 */
function InclusionsPanel({ includes, excludes }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E5DDCB]">
        <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#1F4D3A] mb-3 sm:mb-4">What's included</h3>
        <ul className="space-y-2 text-sm text-[#4A3728]">
          {includes.map((i) => (
            <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#6B8E5A] mt-0.5 flex-shrink-0" strokeWidth={2.5} /> {i}</li>
          ))}
        </ul>
      </div>
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E5DDCB]">
        <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#1F4D3A] mb-3 sm:mb-4">Not included</h3>
        <ul className="space-y-2 text-sm text-[#4A3728]">
          {excludes.map((i) => (
            <li key={i} className="flex items-start gap-2"><X className="w-4 h-4 text-[#C87A50] mt-0.5 flex-shrink-0" strokeWidth={2.5} /> {i}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function TourDetail() {
  const { id } = useParams();
  const { tour, ready } = useTour(id);

  if (!ready) {
    return (
      <main className="pt-32 pb-24 text-center px-6">
        <div className="w-10 h-10 rounded-full border-2 border-[#1F4D3A]/20 border-t-[#1F4D3A] animate-spin mx-auto" />
      </main>
    );
  }

  if (!tour) {
    return (
      <main className="pt-32 pb-24 text-center px-6">
        <h1 className="font-serif text-4xl text-[#1F4D3A] mb-4">Journey not found</h1>
        <Link to="/tours" className="text-[#C87A50] font-semibold hover:underline">← Back to all tours</Link>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-16">
      <TourHero tour={tour} />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12 mt-12 sm:mt-16">
        <div className="lg:col-span-2 space-y-10 sm:space-y-12">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-3 sm:mb-4">The journey</h2>
            <p className="text-[#4A3728] leading-relaxed text-base sm:text-lg">{tour.short}</p>
          </div>

          {tour.tripInfo && <TripInfoPanel tripInfo={tour.tripInfo} duration={tour.duration} />}

          {tour.experiences && tour.experiences.length > 0 && <ExperienceSlots experiences={tour.experiences} />}

          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-4 sm:mb-5">Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {tour.highlights.map((h) => (
                <div key={h} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#E5DDCB]">
                  <div className="w-6 h-6 rounded-full bg-[#F5F0E8] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#1F4D3A]" strokeWidth={3} />
                  </div>
                  <span className="text-sm text-[#4A3728]">{h}</span>
                </div>
              ))}
            </div>
          </div>

          <ItineraryTimeline itinerary={tour.itinerary} />

          <InclusionsPanel includes={tour.includes} excludes={tour.excludes} />

          {tour.priceNote && (
            <div className="bg-[#F5F0E8] border border-[#E5DDCB] rounded-xl p-4 sm:p-5 flex items-start gap-3">
              <Info className="w-4 h-4 mt-0.5 text-[#C87A50] flex-shrink-0" />
              <p className="text-xs sm:text-sm text-[#4A3728] leading-relaxed italic">{tour.priceNote}</p>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <BookingForm tour={tour} buttonLabel="Book This Adventure" />
        </aside>
      </section>
    </main>
  );
}
